import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import T from 'T'
import { HHFormSelectField } from 'components/form-fields/v5'
import { DialogContent, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useForm } from 'react-hook-form'
import HHDialogActions from 'components/common/HHDialogActions'
import CancelButton from 'components/buttons/CancelButton'
import { useGenerateStatementMutation, useLazyGetEarliestStatementDateQuery } from 'api/billing/statementCrud'
import { get } from 'lodash'
import { toast } from 'react-toastify'
import SaveButton from 'components/buttons/SaveButton'
import { CACHE_TAG_ACCOUNT_STATEMENT_LIST } from 'api/cacheTagTypes'
import api from 'api'
import { useDispatch } from 'react-redux'
import {
  generateBillingPeriods,
  periodOptions,
  STATEMENT_MONTHLY_PERIOD,
  STATEMENT_ANNUAL_PERIOD,
  getTerm,
} from 'components/customer-details/content/statements/settings'

const defaultValues = {
  billingPeriod: '',
  period: STATEMENT_MONTHLY_PERIOD,
}

const GenerateStatementDialog = ({ onClose, open, customerDetails }) => {
  const {
    reset,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues,
  })
  const billingPeriodError = get(errors, 'billingPeriod.message', '')
  const periodError = get(errors, 'period.message', '')
  const dispatch = useDispatch()
  const accountId = get(customerDetails, 'id')
  const [generateStatementMutation, { isLoading }] = useGenerateStatementMutation()
  const [getEarliestDate, { data: earliestDateData }] = useLazyGetEarliestStatementDateQuery()
  const watchPeriod = watch('period')
  const isAnnualPeriod = useMemo(() => watchPeriod === STATEMENT_ANNUAL_PERIOD, [watchPeriod])
  const options = useMemo(() => {
    if (!earliestDateData?.earliestStatementDate) return []
    return generateBillingPeriods(earliestDateData.earliestStatementDate, watchPeriod).map(option => ({
      ...option,
      key: option.label,
      value: option.label,
    }))
  }, [watchPeriod, earliestDateData?.earliestStatementDate])

  const handlePeriodChange = () => {
    setValue('billingPeriod', '')
  }

  const onSubmit = ({ billingPeriod, period }) => {
    const selectedOption = options.find(option => option.label === billingPeriod)
    const { startDate, endDate } = selectedOption || {}
    generateStatementMutation({
      startDate,
      endDate,
      accountId,
      term: getTerm(period),
    })
      .unwrap()
      .then(() => {
        toast.success(T.STATEMENT_IS_GENERATING, {
          hideProgressBar: false,
          autoClose: 4000,
          onClose: () => {
            dispatch(api.util.invalidateTags([CACHE_TAG_ACCOUNT_STATEMENT_LIST]))
          },
        })
        onClose()
      })
  }

  useEffect(() => {
    if (open && accountId) {
      getEarliestDate({ accountId })
        .unwrap()
        .then(d => console.log(d))
    }
  }, [open, accountId])

  useEffect(() => {
    if (open) {
      reset(defaultValues)
    }
  }, [open])

  return (
    <HHBaseDialog maxWidth="sm" fullWidth open={open} onClose={onClose}>
      <HHDialogTitle title={T.GENERATE_MONTHLY_STATEMENT} onClose={onClose} showCloseIcon />
      <DialogContent>
        <HHFormSelectField
          sx={{
            mt: 0.8,
          }}
          fullWidth
          name="period"
          deprecatedLabel={false}
          control={control}
          options={periodOptions}
          label={T.PERIOD}
          required
          onChange={handlePeriodChange}
          error={Boolean(periodError)}
          helperText={periodError}
          rules={{
            required: T.THIS_FIELD_IS_REQUIRED,
          }}
        />
        <HHFormSelectField
          error={Boolean(billingPeriodError)}
          helperText={billingPeriodError}
          deprecatedLabel={false}
          label={T.BILLING_PERIOD}
          fullWidth
          required
          rules={{
            required: T.THIS_FIELD_IS_REQUIRED,
          }}
          sx={{
            mt: 2,
          }}
          name="billingPeriod"
          control={control}
          options={options}
          getOptionLabel={option => {
            return (
              <Grid container sx={{ width: '100%' }}>
                <Grid size="grow">
                  <Typography fontWeight="bold">{option.label}</Typography>
                </Grid>
                {!isAnnualPeriod && (
                  <Grid size="grow">
                    <Typography textAlign="right">{option.period}</Typography>
                  </Grid>
                )}
              </Grid>
            )
          }}
        />
      </DialogContent>
      <HHDialogActions>
        <CancelButton onClick={onClose} />
        <SaveButton loading={isLoading} variant="contained" onClick={handleSubmit(onSubmit)}>
          Generate
        </SaveButton>
      </HHDialogActions>
    </HHBaseDialog>
  )
}

GenerateStatementDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  customerDetails: PropTypes.object,
}

export default GenerateStatementDialog
